(() => {
  const root = window.Shopify?.routes?.root || '/';
  const currency = document.body.dataset.currency || 'EUR';

  document.documentElement.classList.add('reveal-ready');

  const revealElements = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    revealElements.forEach((element) => revealObserver.observe(element));
  } else {
    revealElements.forEach((element) => element.classList.add('is-visible'));
  }

  document.querySelectorAll('.editorial-film video').forEach((video) => {
    if (!('IntersectionObserver' in window)) return;
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.15 });
    videoObserver.observe(video);
  });

  const money = (cents) => new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency
  }).format(Number(cents || 0) / 100);

  const moneyMajor = (amount) => new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency
  }).format(Number(amount || 0));

  const closeOverlay = (details) => {
    if (!details) return;
    details.removeAttribute('open');
    if (!document.querySelector('[data-overlay-details][open]')) {
      document.body.classList.remove('overlay-open');
    }
  };

  document.querySelectorAll('[data-overlay-details]').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (!details.open) {
        if (!document.querySelector('[data-overlay-details][open]')) {
          document.body.classList.remove('overlay-open');
        }
        return;
      }

      document.querySelectorAll('[data-overlay-details][open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
      document.body.classList.add('overlay-open');
      requestAnimationFrame(() => details.querySelector('input')?.focus());
    });

    details.querySelectorAll('[data-close-details]').forEach((button) => {
      button.addEventListener('click', () => closeOverlay(details));
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeOverlay(document.querySelector('[data-overlay-details][open]'));
    document.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
  });

  document.querySelectorAll('[data-predictive-search]').forEach((form) => {
    const input = form.querySelector('input[name="q"]');
    const results = form.parentElement.querySelector('[data-predictive-results]');
    let timer;
    let controller;

    input?.addEventListener('input', () => {
      window.clearTimeout(timer);
      controller?.abort();
      const query = input.value.trim();

      if (query.length < 2) {
        results.replaceChildren(Object.assign(document.createElement('p'), {
          textContent: 'Escribe al menos dos letras para buscar.'
        }));
        return;
      }

      timer = window.setTimeout(async () => {
        controller = new AbortController();
        results.setAttribute('aria-busy', 'true');
        try {
          const endpoint = `${form.dataset.predictiveUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`;
          const response = await fetch(endpoint, { signal: controller.signal });
          if (!response.ok) throw new Error('No se pudo completar la búsqueda.');
          const payload = await response.json();
          const products = payload.resources?.results?.products || [];
          results.replaceChildren();

          if (!products.length) {
            results.append(Object.assign(document.createElement('p'), {
              textContent: 'No hemos encontrado productos con ese nombre.'
            }));
            return;
          }

          products.forEach((product) => {
            const link = document.createElement('a');
            link.className = 'predictive-result';
            link.href = product.url;

            if (product.image) {
              const image = document.createElement('img');
              image.src = product.image;
              image.alt = '';
              image.loading = 'lazy';
              link.append(image);
            } else {
              link.append(document.createElement('span'));
            }

            const copy = document.createElement('span');
            const title = document.createElement('span');
            title.textContent = product.title;
            const price = document.createElement('span');
            price.className = 'predictive-result-price';
            price.textContent = moneyMajor(product.price);
            copy.append(title, price);
            link.append(copy);
            results.append(link);
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            results.replaceChildren(Object.assign(document.createElement('p'), {
              textContent: 'La búsqueda no está disponible ahora mismo.'
            }));
          }
        } finally {
          results.removeAttribute('aria-busy');
        }
      }, 220);
    });
  });

  const renderCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });
    document.querySelectorAll('[data-cart-subtotal]').forEach((subtotal) => {
      subtotal.textContent = money(cart.total_price);
    });
    document.querySelectorAll('[data-cart-empty]').forEach((empty) => {
      empty.hidden = cart.item_count > 0;
    });
    document.querySelectorAll('[data-cart-footer]').forEach((footer) => {
      footer.hidden = cart.item_count === 0;
    });

    document.querySelectorAll('[data-cart-items]').forEach((container) => {
      container.replaceChildren();
      cart.items.forEach((item) => {
        const article = document.createElement('article');
        article.className = 'cart-drawer-item';

        const mediaLink = document.createElement('a');
        mediaLink.href = item.url;
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          mediaLink.append(image);
        }

        const copy = document.createElement('div');
        const heading = document.createElement('h3');
        const productLink = document.createElement('a');
        productLink.href = item.url;
        productLink.textContent = item.product_title;
        heading.append(productLink);
        copy.append(heading);

        if (item.variant_title) {
          copy.append(Object.assign(document.createElement('p'), {
            textContent: item.variant_title
          }));
        }
        copy.append(Object.assign(document.createElement('p'), {
          textContent: `${item.quantity} × ${money(item.final_price)}`
        }));
        article.append(mediaLink, copy);
        container.append(article);
      });
    });
  };

  const openCart = (message) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    document.querySelectorAll('[data-overlay-details][open]').forEach((other) => {
      if (other !== drawer) other.removeAttribute('open');
    });
    drawer.open = true;
    document.body.classList.add('overlay-open');
    const status = drawer.querySelector('[data-cart-message]');
    if (status) status.textContent = message || '';
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-quick-add]');
    if (!form || !event.submitter?.matches('[data-add-to-cart]')) return;
    event.preventDefault();

    const button = event.submitter;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Añadiendo…';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'No se pudo añadir la pieza.');
      }
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      renderCart(await cartResponse.json());
      openCart('La pieza se ha añadido a tu selección.');
    } catch (error) {
      const status = form.querySelector('[data-product-status]');
      if (status) status.textContent = error.message;
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const select = form.querySelector('[data-variant-select]');
    const productRoot = form.closest('[data-product-root]');
    if (!select || !productRoot) return;

    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      const available = option.dataset.available === 'true';
      productRoot.querySelectorAll('[data-product-price]').forEach((price) => {
        price.textContent = option.dataset.price;
      });
      const comparePrice = option.dataset.comparePrice || '';
      productRoot.querySelectorAll('[data-product-compare-price]').forEach((price) => {
        price.textContent = comparePrice;
        price.hidden = !comparePrice;
      });
      productRoot.querySelectorAll('[data-product-discount]').forEach((badge) => {
        badge.hidden = !comparePrice;
      });
      productRoot.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        button.disabled = !available;
        button.textContent = available ? 'Añadir al carrito' : 'Agotado';
      });
      productRoot.querySelectorAll('[data-product-status]').forEach((status) => {
        status.textContent = available ? 'Disponible' : 'Agotado temporalmente';
      });
      const url = new URL(window.location.href);
      if (url.pathname.includes('/products/')) {
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    });
  });

  document.querySelectorAll('[data-dialog-open]').forEach((button) => {
    button.addEventListener('click', () => {
      document.getElementById(button.dataset.dialogOpen)?.showModal();
    });
  });
  document.querySelectorAll('[data-dialog-close]').forEach((button) => {
    button.addEventListener('click', () => button.closest('dialog')?.close());
  });
  document.querySelectorAll('dialog').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  });

  document.querySelectorAll('[data-product-recommendations]').forEach((section) => {
    const loadRecommendations = async () => {
      if (!section.dataset.url || section.dataset.loaded === 'true') return;
      section.dataset.loaded = 'true';
      try {
        const response = await fetch(section.dataset.url);
        if (!response.ok) return;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendations = documentFragment.querySelector('[data-product-recommendations]');
        if (recommendations?.children.length) section.replaceChildren(...recommendations.children);
      } catch (_) {
        section.dataset.loaded = 'false';
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        loadRecommendations();
      }, { rootMargin: '300px' });
      observer.observe(section);
    } else {
      loadRecommendations();
    }
  });
})();
